/**
 * Documentos obrigatórios por categoria de veículo (referência: cadastro de veículos do CSA).
 * Mantenha esta regra idêntica em functions/src/vehicleDocumentRequirements.ts.
 */

export type VehicleDocumentGroup = "light" | "collective";

export interface VehicleDocumentRequirement {
  /** Valor gravado em documents.documentType. */
  documentType: string;
  label: string;
  /** "drivers": conferido pela validade da CNH dos motoristas autorizados, não por anexo do veículo. */
  source: "vehicle" | "drivers";
  groups: VehicleDocumentGroup[];
  /** Exigido somente quando o veículo é locado. */
  rentedOnly?: boolean;
  /** Tipos já gravados que também atendem ao requisito (cadastros antigos). */
  alsoAccepts?: string[];
}

export const VEHICLE_DOCUMENT_REQUIREMENTS: readonly VehicleDocumentRequirement[] = [
  { documentType: "registration", label: "CRLV", source: "vehicle", groups: ["light", "collective"], alsoAccepts: ["licensing"] },
  { documentType: "license", label: "CNH", source: "drivers", groups: ["light", "collective"] },
  { documentType: "insurance", label: "Apólice de seguro", source: "vehicle", groups: ["light", "collective"] },
  { documentType: "rental-contract", label: "Contrato de locação", source: "vehicle", groups: ["light", "collective"], rentedOnly: true },
  { documentType: "tachograph-certificate", label: "Certificado do cronotacógrafo", source: "vehicle", groups: ["collective"] },
  { documentType: "opacity-report", label: "Laudo de opacidade", source: "vehicle", groups: ["collective"] }
];

export const VEHICLE_DOCUMENT_GROUP_LABEL: Record<VehicleDocumentGroup, string> = { light: "Veículos leves", collective: "Transporte coletivo" };

/** Dispensa de um documento obrigatório para uma empresa (systemSettings/default.companyDocumentWaivers). */
export interface CompanyDocumentWaiver { companyId: string; documentType: string }

export interface RequirementVehicle { type?: unknown; ownership?: unknown }
export interface RequirementDocument { documentType?: unknown; expiryDate?: unknown; active?: unknown }
export interface RequirementDriver { id?: unknown; name?: unknown; licenseExpiry?: unknown }

export type RequirementState = "ok" | "expiring" | "missing" | "expired";
export interface RequirementStatus {
  requirement: VehicleDocumentRequirement;
  state: RequirementState;
  /** Complemento exibido ao usuário, por exemplo os motoristas com CNH vencida. */
  detail?: string;
}

const normalizedType = (value: unknown) => String(value || "")
  .normalize("NFD")
  .replace(/[̀-ͯ]/g, "")
  .toLocaleLowerCase("pt-BR")
  .replace(/[_-]+/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/** Van, micro-ônibus e ônibus são transporte coletivo; os demais tipos (inclusive os legados) seguem a regra de veículos leves. */
export function vehicleDocumentGroup(type: unknown): VehicleDocumentGroup {
  return /\b(onibus|microonibus|bus|microbus|vans?)\b/.test(normalizedType(type)) ? "collective" : "light";
}

/**
 * Requisitos aplicáveis ao veículo, já descontadas as dispensas por empresa.
 * Com empresa de contexto (por exemplo, na emissão de credencial) vale a dispensa dela;
 * sem contexto, o documento só é dispensado quando todas as empresas do veículo o dispensam.
 */
export function vehicleDocumentRequirements(
  vehicle: RequirementVehicle,
  { companyIds = [], contextCompanyId, waivers = [] }: { companyIds?: string[]; contextCompanyId?: string | null; waivers?: CompanyDocumentWaiver[] } = {}
): VehicleDocumentRequirement[] {
  const group = vehicleDocumentGroup(vehicle.type);
  const scope = contextCompanyId ? [contextCompanyId] : companyIds.filter(Boolean);
  const waived = (documentType: string) => scope.length > 0 && scope.every((companyId) => waivers.some((waiver) => waiver.companyId === companyId && waiver.documentType === documentType));
  return VEHICLE_DOCUMENT_REQUIREMENTS.filter((requirement) =>
    requirement.groups.includes(group) && (!requirement.rentedOnly || vehicle.ownership === "rented") && !waived(requirement.documentType));
}

/** Aceita YYYY-MM-DD, ISO, Date e Timestamp do Firestore; devolve YYYY-MM-DD ou null. */
function dateKey(value: unknown): string | null {
  if (!value) return null;
  const date = typeof value === "object" && typeof (value as { toDate?: unknown }).toDate === "function" ? (value as { toDate: () => Date }).toDate() : value instanceof Date ? value : null;
  if (date) return Number.isNaN(date.getTime()) ? null : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  const text = String(value).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : null;
}

const addDays = (day: string, days: number) => {
  const value = new Date(`${day}T12:00:00Z`);
  value.setUTCDate(value.getUTCDate() + (Number.isFinite(days) ? Math.max(0, days) : 0));
  return value.toISOString().slice(0, 10);
};

/** Documento sem validade informada conta como válido, como no restante do sistema. */
function expiryState(expiryDate: unknown, today: string, warningDays: number): "ok" | "expiring" | "expired" {
  const expiry = dateKey(expiryDate);
  if (!expiry) return "ok";
  if (expiry < today) return "expired";
  return expiry <= addDays(today, warningDays) ? "expiring" : "ok";
}

const STATE_RANK: Record<"ok" | "expiring" | "expired", number> = { ok: 0, expiring: 1, expired: 2 };
const best = (states: Array<"ok" | "expiring" | "expired">) => states.reduce((current, next) => STATE_RANK[next] < STATE_RANK[current] ? next : current);

/** Situação de cada requisito a partir dos documentos ativos do veículo e dos motoristas autorizados. */
export function evaluateVehicleDocuments(
  requirements: VehicleDocumentRequirement[],
  documents: RequirementDocument[],
  drivers: RequirementDriver[],
  { today, warningDays = 30 }: { today: string; warningDays?: number }
): RequirementStatus[] {
  return requirements.map((requirement) => {
    if (requirement.source === "drivers") {
      if (!drivers.length) return { requirement, state: "missing", detail: "Nenhum motorista autorizado" };
      const states = drivers.map((driver) => dateKey(driver.licenseExpiry) ? expiryState(driver.licenseExpiry, today, warningDays) : "expired" as const);
      const irregular = drivers.filter((_, index) => states[index] === "expired").map((driver) => String(driver.name || driver.id || "Motorista"));
      return { requirement, state: best(states), ...(irregular.length ? { detail: `CNH vencida ou sem validade: ${irregular.join(", ")}` } : {}) };
    }
    const accepted = [requirement.documentType, ...(requirement.alsoAccepts || [])];
    const matching = documents.filter((document) => document.active !== false && accepted.includes(String(document.documentType)));
    if (!matching.length) return { requirement, state: "missing" };
    return { requirement, state: best(matching.map((document) => expiryState(document.expiryDate, today, warningDays))) };
  });
}

export const isBlockingRequirement = (status: RequirementStatus) => status.state === "missing" || status.state === "expired";

/** Texto curto de cada pendência que impede a conclusão, por exemplo "CRLV pendente". */
export function requirementIssues(statuses: RequirementStatus[]): string[] {
  return statuses.filter(isBlockingRequirement).map(({ requirement, state }) => `${requirement.label} ${state === "missing" ? "pendente" : "com validade vencida"}`);
}

/** Data local de hoje em YYYY-MM-DD. */
export const todayKey = (now = new Date()) => dateKey(now)!;
